import {useParams} from 'react-router-dom'
import {Edit2, Plug} from 'lucide-react'
import {useI18n} from '@/i18n'
import {useDialog} from '@/hooks/useDialog'
import {useAuth} from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'
import {Card, CardContent, CardDescription, CardTitle} from '@/components/ui/card'
import {Button} from '@/components/ui/button'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {RowCardHeader} from '@/components/shared/RowCardHeader'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {DetailField} from '@/components/shared/DetailField'
import {Stack} from '@/components/shared/Stack'
import {CodeText} from '@/components/shared/CodeText'
import {MutedText} from '@/components/shared/MutedText'
import {EmptyState} from '@/components/shared/EmptyState'
import {Icon} from '@/components/shared/Icon'
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from '@/components/ui/tooltip'
import {formatRelativeTime, formatTimestamp} from '@/lib/date-utils'
import {InboundConnectorType, InboundAuthType} from '@/types/inbound-connector'
import type {RestInboundConfig, ScimInboundConfig} from '@/types/inbound-connector'
import {
    useInboundConnector,
    useRestConfig,
    useScimConfig,
    useScriptInboundConfig,
} from '@/hooks/use-inbound-connectors'
import {ScriptInboundConfigCard} from '@/features/inbound-connectors/components/ScriptInboundConfigCard'
import {RestConfigEditDialog, ScimConfigEditDialog, buildFullUrl} from '@/features/inbound-connectors/components/RestConfigEditDialog'
import {SapHcmInboundConfigCard} from '@/features/inbound-connectors/components/sap-hcm/SapHcmInboundConfigCard'
import {SapHcmInboundConfigEditDialog} from '@/features/inbound-connectors/components/sap-hcm/SapHcmInboundConfigEditDialog'
import {SapSfEcInboundConfigCard} from '@/features/inbound-connectors/components/sap-sf-ec/SapSfEcInboundConfigCard'
import {SapSfEcInboundConfigEditDialog} from '@/features/inbound-connectors/components/sap-sf-ec/SapSfEcInboundConfigEditDialog'
import {useSapHcmConfig, useSapSfEcConfig, useCreateSapHcmConfig, useCreateSapSfEcConfig} from '@/hooks/use-sap-configs'

function authTypeLabel(type: string, i18n: ReturnType<typeof useI18n>): string {
    switch (type) {
        case InboundAuthType.BEARER_TOKEN: return i18n.inboundConnectors.authBearer
        case InboundAuthType.OAUTH2_CLIENT: return i18n.inboundConnectors.authOAuth2
        case InboundAuthType.BASIC_AUTH: return i18n.inboundConnectors.authBasic
        case InboundAuthType.API_KEY: return i18n.inboundConnectors.authApiKey
        case InboundAuthType.MUTUAL_TLS: return i18n.inboundConnectors.authMutualTls
        case InboundAuthType.GOOGLE_SA_JWT: return i18n.inboundConnectors.authGoogleSaJwt
        case InboundAuthType.NONE: return i18n.inboundConnectors.authNone
        default: return type
    }
}

interface RestConfigCardProps {
    config: RestInboundConfig
    lastSyncAt: string | null
    canManage: boolean
    onEdit: () => void
}

function RestConfigCard({config, lastSyncAt, canManage, onEdit}: RestConfigCardProps) {
    const i18n = useI18n()

    return (
        <Card data-testid="connection-section">
            <RowCardHeader>
                <div>
                    <CardTitle data-testid="card-title-connection">
                        {i18n.inboundConnectors.connectionSection}
                    </CardTitle>
                    <CardDescription>{i18n.inboundConnectors.connectionDescription}</CardDescription>
                </div>
                {canManage && (
                    <Button data-testid="btn-edit-rest-config" size="sm" variant="outline" onClick={onEdit}>
                        <Icon icon={Edit2} className="mr-2"/>
                        {i18n.common.edit}
                    </Button>
                )}
            </RowCardHeader>
            <CardContent>
                <Stack gap="md">
                    <DetailGrid columns={2} data-testid="auth-section">
                        <DetailField label={i18n.inboundConnectors.baseUrl} value={<CodeText>{config.baseUrl}</CodeText>} fullWidth/>
                        <DetailField label={i18n.inboundConnectors.itemsEndpoint} value={<CodeText>{config.itemsEndpoint}</CodeText>} fullWidth/>
                        <DetailField label={i18n.inboundConnectors.queryParams} value={<CodeText>{config.queryFilter || '—'}</CodeText>} fullWidth/>
                        <DetailField label={i18n.inboundConnectors.fullUrl} value={<CodeText>{buildFullUrl(config.baseUrl, config.itemsEndpoint, config.queryFilter)}</CodeText>} fullWidth/>
                        <DetailField label={i18n.inboundConnectors.itemsJsonPath} value={<CodeText>{config.itemsJsonPath || '—'}</CodeText>}/>
                        <DetailField label={i18n.inboundConnectors.responseFormat} value={<Badge variant="outline" data-testid="badge-response-format">{config.responseFormat}</Badge>}/>
                        <DetailField label={i18n.inboundConnectors.totalCountJsonPath} value={<CodeText>{config.totalCountJsonPath || '—'}</CodeText>}/>
                        <DetailField label={i18n.inboundConnectors.authType} value={<Badge variant="outline">{authTypeLabel(config.authType, i18n)}</Badge>}/>
                        {config.authType === InboundAuthType.OAUTH2_CLIENT && (<>
                            <DetailField label={i18n.inboundConnectors.clientId} value={config.clientId || '—'}/>
                            <DetailField label={i18n.inboundConnectors.tokenUrl} value={<CodeText>{config.tokenUrl || '—'}</CodeText>}/>
                            <DetailField label={i18n.inboundConnectors.oauthScope} value={config.scope || '—'}/>
                        </>)}
                        {config.authType === InboundAuthType.BASIC_AUTH && (<>
                            <DetailField label={i18n.inboundConnectors.username} value={config.username || '—'}/>
                        </>)}
                        {config.authType === InboundAuthType.API_KEY && (<>
                            <DetailField label={i18n.inboundConnectors.apiKeyHeader} value={config.apiKeyHeader || '—'}/>
                        </>)}
                        {config.authType === InboundAuthType.GOOGLE_SA_JWT && (<>
                            <DetailField
                                label={i18n.inboundConnectors.googleDelegatedUser}
                                value={<span data-testid="google-delegated-user-value">{config.googleDelegatedUser || '—'}</span>}
                            />
                            <DetailField
                                label={i18n.inboundConnectors.googleSaEmail}
                                value={<span data-testid="google-sa-email-value">{config.googleSaEmail || '—'}</span>}
                            />
                            <DetailField
                                label={i18n.inboundConnectors.googleSaKeySecretRef}
                                value={canManage
                                    ? <span data-testid="google-sa-key-secret-ref-value"><CodeText>{config.googleSaKeySecretRef || '—'}</CodeText></span>
                                    : <span data-testid="google-sa-key-secret-ref-masked">{i18n.inboundConnectors.googleSecretMasked}</span>
                                }
                            />
                            <DetailField
                                label={i18n.inboundConnectors.googleTokenScope}
                                value={<span data-testid="google-token-scope-value">{config.googleTokenScope || '—'}</span>}
                                fullWidth
                            />
                        </>)}
                    </DetailGrid>
                    <DetailGrid columns={2} data-testid="pagination-section">
                        <DetailField label={i18n.inboundConnectors.paginationStyle} value={config.paginationStyle}/>
                        <DetailField label={i18n.inboundConnectors.pageSize} value={String(config.pageSize)}/>
                        <DetailField label={i18n.inboundConnectors.connectTimeout} value={String(config.connectTimeoutMs)}/>
                        <DetailField label={i18n.inboundConnectors.readTimeout} value={String(config.readTimeoutMs)}/>
                        <DetailField label={i18n.inboundConnectors.maxRetries} value={String(config.maxRetries)}/>
                    </DetailGrid>
                    <DetailGrid columns={2} data-testid="last-sync-row">
                        <DetailField
                            label={i18n.inboundConnectors.lastFullSync}
                            value={lastSyncAt
                                ? (
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <span data-testid="last-sync-relative">
                                                    {formatRelativeTime(lastSyncAt)}
                                                </span>
                                            </TooltipTrigger>
                                            <TooltipContent>{formatTimestamp(lastSyncAt)}</TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                )
                                : <MutedText>{i18n.inboundConnectors.neverSynced}</MutedText>
                            }
                        />
                    </DetailGrid>
                </Stack>
            </CardContent>
        </Card>
    )
}

interface ScimConfigCardProps {
    scimConfig: ScimInboundConfig | undefined
    canManage: boolean
    onEdit: () => void
}

function ScimConfigCard({scimConfig, canManage, onEdit}: ScimConfigCardProps) {
    const i18n = useI18n()
    if (!scimConfig) return null

    return (
        <Card data-testid="scim-section">
            <RowCardHeader>
                <div>
                    <CardTitle data-testid="card-title-scim-config">{i18n.inboundConnectors.editScimConfig}</CardTitle>
                    <CardDescription>{i18n.inboundConnectors.editScimConfigDescription}</CardDescription>
                </div>
                {canManage && (
                    <Button data-testid="btn-edit-scim-config" size="sm" variant="outline" onClick={onEdit}>
                        <Icon icon={Edit2} className="mr-2"/>
                        {i18n.common.edit}
                    </Button>
                )}
            </RowCardHeader>
            <CardContent>
                <DetailGrid columns={2}>
                    <DetailField
                        label={i18n.inboundConnectors.scimFilter}
                        value={scimConfig.scimFilter ? <CodeText>{scimConfig.scimFilter}</CodeText> : <MutedText>{'—'}</MutedText>}
                        fullWidth
                    />
                    <DetailField
                        label={i18n.inboundConnectors.enterpriseExtension}
                        value={
                            <Badge variant={scimConfig.enterpriseExtEnabled ? BadgeVariant.SUCCESS : 'outline'}>
                                {scimConfig.enterpriseExtEnabled ? i18n.inboundConnectors.enabled : i18n.inboundConnectors.disabled}
                            </Badge>
                        }
                    />
                </DetailGrid>
            </CardContent>
        </Card>
    )
}

export function ConnectionSubPage() {
    const i18n = useI18n()
    const {id} = useParams<{id: string}>()
    const {hasPermission} = useAuth()
    const canManage = hasPermission(permissions.inboundConnectors.manage)
    const canExecute = hasPermission(permissions.inboundConnectors.execute)

    const {data: connector, isLoading: connectorLoading} = useInboundConnector(id)
    const isScriptConnector = connector?.connectorType === InboundConnectorType.SCRIPT
    const isSapHcm = connector?.connectorType === InboundConnectorType.SAP_HCM
    const isSapSfEc = connector?.connectorType === InboundConnectorType.SAP_SF_EC
    const isScim = connector?.connectorType === InboundConnectorType.SCIM_2_0
    const isRestLike = !isScriptConnector && !isSapHcm && !isSapSfEc && !isScim

    const {data: restConfig, isLoading: restLoading} = useRestConfig(
        isRestLike && connector ? connector.id : undefined
    )
    const {data: scriptConfig, isLoading: scriptLoading} = useScriptInboundConfig(
        isScriptConnector && connector ? connector.id : undefined
    )
    const {data: scimConfig, isLoading: scimLoading} = useScimConfig(
        isScim && connector ? connector.id : undefined
    )
    const {data: sapHcmConfig, isLoading: sapHcmLoading} = useSapHcmConfig(
        isSapHcm && connector ? connector.id : undefined
    )
    const {data: sapSfEcConfig, isLoading: sapSfEcLoading} = useSapSfEcConfig(
        isSapSfEc && connector ? connector.id : undefined
    )

    const restConfigDialog = useDialog()
    const scimConfigDialog = useDialog()
    const sapHcmConfigDialog = useDialog()
    const sapSfEcConfigDialog = useDialog()
    const createSapHcmMutation = useCreateSapHcmConfig(connector?.id ?? '')
    const createSapSfEcMutation = useCreateSapSfEcConfig(connector?.id ?? '')

    const primaryLoading = isScriptConnector ? scriptLoading
        : isSapHcm ? sapHcmLoading
        : isSapSfEc ? sapSfEcLoading
        : isScim ? scimLoading
        : restLoading
    if (connectorLoading || primaryLoading) {
        return (
            <Card data-testid="connection-subpage-skeleton">
                <CardContent>
                    <MutedText>{i18n.common.loading}</MutedText>
                </CardContent>
            </Card>
        )
    }

    if (!connector) return null

    // WHY: for connectors that fetch their config via GET /...-config, a fresh
    // connector returns 404 (no config row yet). The hook records that as
    // `error` (not data). Treat "no config" as empty regardless of whether
    // the API returned 404-error or empty-success — both are the "configure
    // me" empty state from the user's perspective.
    const primaryMissing = isScriptConnector ? !scriptConfig
        : isSapHcm ? !sapHcmConfig
        : isSapSfEc ? !sapSfEcConfig
        : isScim ? !scimConfig
        : !restConfig
    if (primaryMissing) {
        const sapCreateAction = canManage && (isSapHcm || isSapSfEc) ? (
            <Button
                data-testid="btn-create-sap-config"
                onClick={() => {
                    if (isSapHcm) createSapHcmMutation.mutate({})
                    else if (isSapSfEc) createSapSfEcMutation.mutate({})
                }}
                disabled={createSapHcmMutation.isPending || createSapSfEcMutation.isPending}
            >
                {i18n.common.create}
            </Button>
        ) : undefined
        return (
            <Card data-testid="connection-subpage-empty">
                <CardContent>
                    <EmptyState
                        icon={Plug}
                        title={i18n.inboundConnectors.connectionEmptyState}
                        action={sapCreateAction}
                        data-testid="no-config-banner"
                    />
                </CardContent>
            </Card>
        )
    }

    return (
        <Stack gap="lg" data-testid="connection-subpage">
            {isScriptConnector && (
                <ScriptInboundConfigCard
                    connectorId={connector.id}
                    canManage={canManage}
                    canExecute={canExecute}
                />
            )}
            {isSapHcm && sapHcmConfig && (
                <SapHcmInboundConfigCard
                    config={sapHcmConfig}
                    canManage={canManage}
                    onEdit={sapHcmConfigDialog.open}
                />
            )}
            {isSapSfEc && sapSfEcConfig && (
                <SapSfEcInboundConfigCard
                    config={sapSfEcConfig}
                    canManage={canManage}
                    onEdit={sapSfEcConfigDialog.open}
                />
            )}
            {isRestLike && restConfig && (
                <RestConfigCard
                    config={restConfig}
                    lastSyncAt={connector.lastSyncAt}
                    canManage={canManage}
                    onEdit={restConfigDialog.open}
                />
            )}
            {connector.connectorType === InboundConnectorType.SCIM_2_0 && (
                <ScimConfigCard
                    scimConfig={scimConfig}
                    canManage={canManage}
                    onEdit={scimConfigDialog.open}
                />
            )}
            {isRestLike && restConfig && (
                <RestConfigEditDialog
                    connectorId={connector.id}
                    config={restConfig}
                    open={restConfigDialog.isOpen}
                    onClose={restConfigDialog.close}
                />
            )}
            {scimConfig && (
                <ScimConfigEditDialog
                    connectorId={connector.id}
                    config={scimConfig}
                    open={scimConfigDialog.isOpen}
                    onClose={scimConfigDialog.close}
                />
            )}
            {isSapHcm && sapHcmConfig && (
                <SapHcmInboundConfigEditDialog
                    connectorId={connector.id}
                    config={sapHcmConfig}
                    open={sapHcmConfigDialog.isOpen}
                    onClose={sapHcmConfigDialog.close}
                />
            )}
            {isSapSfEc && sapSfEcConfig && (
                <SapSfEcInboundConfigEditDialog
                    connectorId={connector.id}
                    config={sapSfEcConfig}
                    open={sapSfEcConfigDialog.isOpen}
                    onClose={sapSfEcConfigDialog.close}
                />
            )}
        </Stack>
    )
}
